import { RESIDENTIAL_UNIT_TYPE_OPTIONS } from 'components/filterBar/residentialUnitTypes'
import { TFunction } from 'i18next'
import { Filters } from 'types/general'

const BEDROOM_LABEL_KEYS: Record<string, string> = {
  studio: 'createAd.flow.rooms.studio',
  '2.5': 'createAd.flow.rooms.2_5',
  '3.5': 'createAd.flow.rooms.3_5',
  '4.5': 'createAd.flow.rooms.4_5',
  '5.5': 'createAd.flow.rooms.5_5',
  '5.5+': 'createAd.flow.rooms.5_5p',
  '6.5+': 'createAd.flow.rooms.6_5p',
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('fr-CA', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value)
}

/** Human-readable chips summarizing currently applied listing filters. */
export function buildActiveFilterSummary(filters: Filters, t: TFunction): string[] {
  const parts: string[] = []

  const city = filters.location?.city?.trim()
  if (city) parts.push(city)

  const neighborhoods = (filters.location?.neighborhoods ?? [])
    .map(value => value.trim())
    .filter(Boolean)
  if (neighborhoods.length) {
    parts.push(...neighborhoods)
  } else if (filters.location?.neighborhood?.trim()) {
    parts.push(filters.location.neighborhood.trim())
  }

  const zip = filters.location?.zip_code?.trim()
  if (zip) parts.push(zip)

  const bedroom = filters.bedrooms?.[0]
  if (bedroom) {
    const key = BEDROOM_LABEL_KEYS[bedroom]
    parts.push(key ? t(key) : bedroom)
  }

  const hasMin = filters.priceMin !== undefined && Number.isFinite(filters.priceMin)
  const hasMax = filters.priceMax !== undefined && Number.isFinite(filters.priceMax)
  if (hasMin && hasMax) {
    parts.push(`${formatMoney(filters.priceMin!)}–${formatMoney(filters.priceMax!)} $`)
  } else if (hasMin) {
    parts.push(`${t('listing.empty.priceFrom')} ${formatMoney(filters.priceMin!)} $`)
  } else if (hasMax) {
    parts.push(`${t('listing.empty.priceUpTo')} ${formatMoney(filters.priceMax!)} $`)
  }

  for (const value of filters.unitType ?? []) {
    const option = RESIDENTIAL_UNIT_TYPE_OPTIONS.find(item => item.value === value)
    parts.push(option ? t(option.label) : value)
  }

  if (filters.booleans?.petFriendly) {
    parts.push(t('listing.empty.petsAccepted'))
  }

  return parts
}

export const RESTORE_FILTERS_STORAGE_KEY = 'easybail_restore_listing_filters'

export function saveFiltersForRestore(filters: Filters): void {
  try {
    sessionStorage.setItem(RESTORE_FILTERS_STORAGE_KEY, JSON.stringify(filters))
  } catch {
    // Ignore quota / private mode failures.
  }
}

export function consumeRestoredFilters(): Filters | null {
  try {
    const raw = sessionStorage.getItem(RESTORE_FILTERS_STORAGE_KEY)
    if (!raw) return null
    sessionStorage.removeItem(RESTORE_FILTERS_STORAGE_KEY)
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    return parsed as Filters
  } catch {
    return null
  }
}

export function bumpPriceMax(priceMax: number): number {
  const bumped = Math.ceil(priceMax * 1.1)
  // Round up to nearest 50 for a cleaner CTA.
  return Math.ceil(bumped / 50) * 50
}

export function filtersWithoutNeighborhoods(filters: Filters): Filters {
  return {
    ...filters,
    location: {
      ...filters.location,
      neighborhoods: [],
      neighborhood: '',
    },
  }
}

export function filtersWithBumpedPriceMax(filters: Filters, nextMax: number): Filters {
  return {
    ...filters,
    priceMax: nextMax,
    price: [],
  }
}
